/**
 * AnchorSkillEffect — Astral rift-path active: placed gravity well.
 *
 * Places a gravity well at a targeted position (up to 4 tiles away). Unlike
 * Rift/Implosion (which lock the player to the well while active), Anchor's
 * active phase is instantaneous — the well is placed, the skill immediately
 * enters its 8000ms cooldown, and the well persists for 6000ms in
 * GravityPullSystem independently. The player can move freely while the
 * well pulls enemies.
 *
 * Twin Anchors (anchor_upgrade_1): reads resolver.getMaxEffect('anchor',
 * 'anchorCount'). If 2, places a second well at the midpoint between the
 * player and the target.
 *
 * Warp Geometry (anchor_passive): the passive queries getActiveWellPositions()
 * on every AstralTeleportSystem teleport event to detect proximity.
 *
 * Design values (from design-decisions.md § Astral Base Values):
 * - Pull radius: 2 tiles
 * - Pull strength: 2 tiles/sec
 * - Well duration: 6000ms
 * - Cooldown: 8000ms (long tier)
 * - Placement range: 4 tiles
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § anchor
 * @see design-decisions.md § Astral Base Values
 * @see ADR-064 (attunement shared systems architecture)
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';
import type { GravityWellConfig } from '../../../systems/GravityPullSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AnchorGravityDeps {
  addWell(config: GravityWellConfig): string;
  removeWell(wellId: string): void;
  /** Returns all currently-active wells (used to filter expired wells). */
  getAll(): ReadonlyArray<Readonly<GravityWellConfig>>;
  /**
   * Subscribe to natural well expiration events.
   * Not called when a well is manually removed via removeWell().
   * Optional — when absent, Tremor Anchor upgrade has no effect.
   */
  onWellExpired?(
    callback: (wellId: string, position: { x: number; y: number }, sourceSkillId: string) => void,
  ): () => void;
}

export interface AnchorDeps {
  readonly resolver: {
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly gravityPull: AnchorGravityDeps;
  readonly targeting: {
    getTargetPosition(): { x: number; y: number };
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  /**
   * Enemy spatial query — required for Tremor Anchor detonation.
   * Optional: only needed when anchor_upgrade_2 is active.
   */
  readonly enemies?: {
    getEnemiesInRadius(position: { x: number; y: number }, radiusTiles: number): string[];
  };
  /**
   * Combat callbacks — required for Tremor Anchor detonation.
   * Optional: only needed when anchor_upgrade_2 is active.
   * applyStatusEffect returns true if applied, false if blocked by immunity.
   */
  readonly combat?: {
    dealDamage(enemyId: string, damage: number): void;
    applyStatusEffect(
      entityId: string,
      defId: string,
      opts: { durationMs: number; sourceSkillId: string },
    ): boolean;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AnchorConfig {
  readonly cooldownMs: number;
  readonly basePullRadiusTiles: number;
  readonly basePullStrengthTilesPerSec: number;
  readonly baseWellDurationMs: number;
  readonly placementRangeTiles: number;
}

const DEFAULT_CONFIG: AnchorConfig = {
  cooldownMs: 8000,
  basePullRadiusTiles: 2,
  basePullStrengthTilesPerSec: 2,
  baseWellDurationMs: 6000,
  placementRangeTiles: 4,
};

import { TILE_SIZE } from '../../../utils/Constants.js';


// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AnchorSkillEffect extends PhasedSkillEffect {
  override readonly skillId = 'anchor';

  private readonly deps: AnchorDeps;
  readonly config: AnchorConfig;

  private _activationsCount = 0;
  /** IDs of wells placed in the most recent activation. */
  private _placedWellIds: string[] = [];
  /** Positions of wells placed in the most recent activation (parallel to _placedWellIds). */
  private _placedWellPositions: Array<{ x: number; y: number }> = [];
  /** Counts detonations fired by the Tremor Anchor upgrade (natural expiry explosions). */
  private _detonationCount = 0;
  /** Unsubscribe function for GravityPullSystem well-expiry callback. */
  private _unsubscribeWellExpiry: (() => void) | null = null;

  constructor(deps: AnchorDeps, config: Partial<AnchorConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Subscribe to well-expiry events so Tremor Anchor (anchor_upgrade_2) can
    // fire detonations on natural expiry. The subscription is unconditional;
    // the handler gates on the upgrade being unlocked at runtime.
    if (deps.gravityPull.onWellExpired) {
      this._unsubscribeWellExpiry = deps.gravityPull.onWellExpired(
        this._onWellExpired.bind(this),
      );
    }
  }

  // --- State getters ---

  get activationsCount(): number { return this._activationsCount; }
  get placedWellIds(): readonly string[] { return this._placedWellIds; }
  /** Number of Tremor Anchor detonations fired so far. */
  get detonationCount(): number { return this._detonationCount; }

  /**
   * Returns positions of all currently-active wells placed by this Anchor.
   * Filters against GravityPullSystem's live well list to exclude expired wells.
   * Queried by WarpGeometryPassiveSkillEffect on every teleport event.
   */
  getActiveWellPositions(): ReadonlyArray<{ x: number; y: number }> {
    const liveIds = new Set(
      this.deps.gravityPull.getAll()
        .filter(w => w.sourceSkillId === 'anchor')
        .map(w => w.id),
    );
    return this._placedWellIds
      .map((id, i) => ({ id, pos: this._placedWellPositions[i]! }))
      .filter(({ id }) => liveIds.has(id))
      .map(({ pos }) => ({ ...pos }));
  }

  /**
   * Returns positions and pull radii of all currently-active wells placed by this Anchor.
   * Used by WarpGeometryPassiveSkillEffect for proximity detection on each teleport event.
   * The pull radius is taken from the base config — no upgrades modify it.
   */
  getActiveWells(): ReadonlyArray<{ position: { x: number; y: number }; pullRadiusTiles: number }> {
    const liveIds = new Set(
      this.deps.gravityPull.getAll()
        .filter(w => w.sourceSkillId === 'anchor')
        .map(w => w.id),
    );
    return this._placedWellIds
      .map((id, i) => ({ id, pos: this._placedWellPositions[i]! }))
      .filter(({ id }) => liveIds.has(id))
      .map(({ pos }) => ({ position: { ...pos }, pullRadiusTiles: this.config.basePullRadiusTiles }));
  }

  // --- PhasedSkillEffect implementation ---

  protected override readonly phaseSequence: readonly SkillPhase[] = ['active', 'cooldown'];

  protected override getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      // Active phase is instantaneous: wells are placed in onPhaseEnter,
      // then the skill immediately advances to the cooldown phase on the next tick.
      case 'active': return 0;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this._placeWells();
    }
  }

  // --- Tremor Anchor (anchor_upgrade_2) ---

  /**
   * Called by GravityPullSystem when any well expires naturally (not via removeWell).
   *
   * If anchor_upgrade_2 is unlocked and the well belongs to Anchor, deals
   * expirationDamage to all enemies within expirationRadiusTiles, then
   * attempts to apply a stagger of expirationStaggerMs via StatusEffectSystem.
   * The stagger application respects the 500ms immunity window — if the
   * target was recently staggered, the CC is blocked but the damage still lands.
   */
  private _onWellExpired(
    _wellId: string,
    position: { x: number; y: number },
    sourceSkillId: string,
  ): void {
    // Only handle wells this skill placed.
    if (sourceSkillId !== 'anchor') return;

    // Gate on Tremor Anchor being unlocked — check the upgrade's effect key.
    const damage = this.deps.resolver.getMaxEffect('anchor', 'expirationDamage');
    if (damage === undefined) return;

    const staggerMs = this.deps.resolver.getMaxEffect('anchor', 'expirationStaggerMs') ?? 300;
    const radiusTiles = this.deps.resolver.getMaxEffect('anchor', 'expirationRadiusTiles') ?? 1;

    if (!this.deps.enemies || !this.deps.combat) return;

    const targets = this.deps.enemies.getEnemiesInRadius(position, radiusTiles);
    for (const enemyId of targets) {
      this.deps.combat.dealDamage(enemyId, damage);
      this.deps.combat.applyStatusEffect(enemyId, 'stagger', {
        durationMs: staggerMs,
        sourceSkillId: 'anchor_upgrade_2',
      });
    }

    this._detonationCount++;
  }

  // --- Internal helpers ---

  private _placeWells(): void {
    const from = this.deps.player.getPosition();
    const target = this.deps.targeting.getTargetPosition();
    const dx = target.x - from.x;
    const dy = target.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxRangePx = this.config.placementRangeTiles * TILE_SIZE;

    // Clamp target to placement range.
    let tx: number;
    let ty: number;
    if (dist <= 0.001 || dist <= maxRangePx) {
      tx = target.x;
      ty = target.y;
    } else {
      tx = from.x + (dx / dist) * maxRangePx;
      ty = from.y + (dy / dist) * maxRangePx;
    }

    // How many wells to place (Twin Anchors upgrade raises this to 2).
    const anchorCount = this.deps.resolver.getMaxEffect('anchor', 'anchorCount') ?? 1;

    this._placedWellIds = [];
    this._placedWellPositions = [];

    // Well 1: at the clamped target position.
    const id1 = `anchor_well_${this._activationsCount}_0`;
    this.deps.gravityPull.addWell({
      id: id1,
      position: { x: tx, y: ty },
      pullRadiusTiles: this.config.basePullRadiusTiles,
      pullStrengthTilesPerSec: this.config.basePullStrengthTilesPerSec,
      durationMs: this.config.baseWellDurationMs,
      sourceSkillId: 'anchor',
    });
    this._placedWellIds.push(id1);
    this._placedWellPositions.push({ x: tx, y: ty });

    // Well 2 (Twin Anchors): at the midpoint between player and target.
    if (anchorCount >= 2) {
      const mx = (from.x + tx) / 2;
      const my = (from.y + ty) / 2;
      const id2 = `anchor_well_${this._activationsCount}_1`;
      this.deps.gravityPull.addWell({
        id: id2,
        position: { x: mx, y: my },
        pullRadiusTiles: this.config.basePullRadiusTiles,
        pullStrengthTilesPerSec: this.config.basePullStrengthTilesPerSec,
        durationMs: this.config.baseWellDurationMs,
        sourceSkillId: 'anchor',
      });
      this._placedWellIds.push(id2);
      this._placedWellPositions.push({ x: mx, y: my });
    }

    this._activationsCount++;
  }

  override destroy(): void {
    this._unsubscribeWellExpiry?.();
    this._unsubscribeWellExpiry = null;
    for (const wellId of this._placedWellIds) {
      this.deps.gravityPull.removeWell(wellId);
    }
    this._placedWellIds = [];
    this._placedWellPositions = [];
    super.destroy();
  }
}
